import axios, { AxiosError, type AxiosInstance } from "axios";

import type { ApiErrorBody } from "@/types/api";

const BACKEND_URL: string = process.env.REACT_APP_BACKEND_URL ?? "";
export const API_BASE = `${BACKEND_URL}/api`;

const api: AxiosInstance = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hs_token");
  const tenantId = localStorage.getItem("hs_tenant_id");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (tenantId) config.headers["X-Tenant-Id"] = tenantId;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err?.response?.status === 401 && !err.config?.url?.includes("/auth/login")) {
      localStorage.removeItem("hs_token");
      localStorage.removeItem("hs_tenant_id");
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/signup") && path !== "/") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export default api;

/** A 401 that only needs a second factor, rather than a wrong password. */
export function twoFactorRequired(err: unknown): boolean {
  const e = err as AxiosError | undefined;
  if (e?.response?.status !== 401) return false;
  const headers = e.response.headers as Record<string, string> | undefined;
  return Boolean(headers?.["x-2fa-required"]);
}

/**
 * The specific reasons a password was rejected, if that is why this failed.
 *
 * Returned separately from humanError because these belong beside the field
 * as a list, not in a toast that disappears while someone is still typing.
 */
export function passwordProblems(err: unknown): string[] {
  const body = (err as AxiosError<ApiErrorBody> | undefined)?.response?.data;
  const detail = body?.error;
  if (detail && typeof detail === "object" && Array.isArray(detail.problems)) {
    return detail.problems;
  }
  return [];
}

/**
 * A sentence worth showing someone.
 *
 * The server's message is preferred over anything invented here, since it is
 * the only party that knows what actually went wrong. The fallbacks exist for
 * the cases where there is no response at all -- offline, DNS, a timeout --
 * where "Network Error" is genuinely the most useful thing to say.
 */
export function humanError(err: unknown): string {
  const e = err as AxiosError<ApiErrorBody> | undefined;
  const data = e?.response?.data;

  if (typeof data === "string") return data;
  if (data?.error) {
    return typeof data.error === "string" ? data.error : data.error.error;
  }
  if (typeof data?.detail === "string") return data.detail;
  return e?.message ?? "Something went wrong";
}
